<template>
  <b-container class="w-75 mt-4">
    <div class="bg-white rounded-lg p-4">
      <h5><b-icon-sliders /> Set your Preferences</h5>
      <div v-if="isLoading" class="bg-light w-100 p-5 rounded-lg d-flex flex-column align-items-center" v-test="'loading'">
        <b-spinner />
        <h4 class="mt-3">Loading your preferences...</h4>
      </div>
      <div v-else-if="failedToLoad">
        <b-row align-v="center" class="ml-0 text-center">
          <p class="mb-0">
            We encountered a problem loading your preferences. Please try again.
          </p>
          <b-button variant="light" @click="setupPreferences" class="ml-2">
            <b-icon-arrow-clockwise />
          </b-button>
        </b-row>
      </div>
      <div v-else>
        <div class="mt-3 mb-4">
          <b-checkbox
            v-model="sameDayMatching"
            @change="unsetLookForward"
          >
            <span v-infinity-tooltip="'Look back to the start of the day for a match and then look forward to the end of the same day.'">
              Same-Day Matching
            </span>
          </b-checkbox>
        </div>
        <div class="mb-4">
          <b-checkbox
            v-model="lookForward"
            :disabled="sameDayMatching"
          >
            <span v-infinity-tooltip="'If no match is found within the set lookback window then look forward 24 hours.'">
              Look Forward
            </span>
          </b-checkbox>
        </div>
        <div class="mb-4">
          <b-checkbox
            v-model="excludeDatetimeOnly"
          >
            <span v-infinity-tooltip="'If an entry has only a date or date/time to search on, then do not try to match it.'">
              Ignore Date/Time Only Entries
            </span>
          </b-checkbox>
        </div>
        <div class="mb-4">
          <p>
            How far back would you like to check for a match?
          </p>
          <b-form-select
            @change="updateLookback"
            :value="lookbackInterval"
            :disabled="sameDayMatching"
            v-test="'lookback hours select'"
          >
            <b-form-select-option
              v-for="lookbackInterval in lookbackIntervalOptions"
              :value="lookbackInterval"
              :key="lookbackInterval.text"
              v-test="`lookback hours ${lookbackInterval.text}`"
            >
              {{ lookbackInterval.text }}
            </b-form-select-option>
          </b-form-select>
        </div>
        <div v-if="lookbackHours === 0" class="mb-4">
          <p>
            How many minutes and seconds do you want to look back for?
          </p>
          <div class="d-flex">
            <div class="mr-3">
              <label for="lookbackSecondsMins">Minutes</label>
              <b-form-spinbutton
                class="ml-3"
                id="lookbackSecondsMins"
                max="60"
                min="0"
                v-model="lookbackSecondsInputMins"
                v-test="'lookback seconds minutes'"
                inline />
            </div>
            <div>
              <label for="lookbackSecondsSecs">Seconds</label>
              <b-form-spinbutton
                class="ml-3"
                id="lookbackSecondsSecs"
                :max="lookbackSecondsInputMins === 60 ? 0 : 59"
                min="0"
                v-model="lookbackSecondsInputSecs"
                v-test="'lookback seconds seconds'"
                inline />
            </div>
          </div>
        </div>
        <div class="mb-4">
          <p>
            Which tracking pools / fixed numbers do you want to include in matching?
          </p>
          <b-form-select @change="updateNumberScope" :value="numberScope">
            <b-form-select-option v-for="numberScope in NumberScope" :value="numberScope" :key="numberScope">{{ numberScope }}</b-form-select-option>
          </b-form-select>
        </div>
        <div>
          <p>
            Email address for SFTP upload notifications
          </p>
          <b-form-tags
            @input="updateNotificationEmails"
            :value="notificationEmails"
            :tag-validator="validateEmail"
            input-type="email"
            v-test="'notification emails'"
            invalid-tag-text="invalid email address"
            duplicate-tag-text="duplicate email addresses"
            placeholder="Add email address..."
          />
        </div>
      </div>
    </div>
    <div v-if="!(isLoading || failedToLoad) && numberScope === NumberScope.SPECIFIC">
      <div class="bg-white rounded-lg p-4 mt-4" v-test="'include tracking pools'">
        <h5><b-icon-collection-fill /> Tracking Pools</h5>
        <p>
          Select tracking pools to include {{ trackingPoolConfig.excludeFixedNumbers ? 'only pool' : 'both pool and fixed' }} numbers in matching.
        </p>
        <CheckboxGroup
          :type="CheckboxGroupType.Pools"
          :options="trackingPoolOptions"
          :preferences="trackingPoolConfig"
          :allValues="trackingPoolIds"
        ></CheckboxGroup>
      </div>
      <div v-if="trackingPoolConfig.excludeFixedNumbers" class="bg-white rounded-lg p-4 mt-4" v-test="'include fixed numbers'">
        <h5><b-icon-telephone-fill /> Include Specific Fixed Numbers</h5>
        <p>
          Select specific fixed numbers from your tracking pools to include in matching.
        </p>
        <CheckboxGroup
          :type="CheckboxGroupType.FixedNumbers"
          :options="fixedNumberOptions"
          :preferences="fixedNumberConfig"
          :allValues="fixedNumberIds"
        ></CheckboxGroup>
      </div>
    </div>
    <div class="mt-3 d-flex">
      <b-button :disabled="isLoading || failedToLoad" @click="doSave" class="ml-auto" variant="success" v-test="'save button'"><b-spinner v-if="isLoading" small /> Save</b-button>
    </div>
  </b-container>
</template>

<script lang="ts">
import { Component, Vue, Watch } from 'vue-property-decorator'
import {
  BIconArrowClockwise,
  BIconCollectionFill,
  BIconSliders,
  BIconTelephoneFill,
  BSpinner,
  BFormSpinbutton,
  ButtonPlugin,
  FormInputPlugin,
  FormSelectPlugin,
  InputGroupPlugin,
  BFormTags,
  ToastPlugin
} from 'bootstrap-vue'
import CheckboxGroup, { CheckboxGroupType } from '@/components/CheckboxGroup.vue'
import { NumberScope, LookbackIntervals, Lookback, UpdatePreferenceRequest } from '@/models/Preference'
import TrackingPool from '@/models/TrackingPool'
import FixedNumber from '@/models/FixedNumber'

Vue.use(ButtonPlugin)
Vue.use(FormSelectPlugin)
Vue.use(FormInputPlugin)
Vue.use(InputGroupPlugin)
Vue.use(ToastPlugin)

export class PreferencesConfig {
  selected!: number[]
  excludeFixedNumbers!: boolean
}

@Component({
  components: {
    BSpinner,
    BIconSliders,
    BIconArrowClockwise,
    BIconTelephoneFill,
    BIconCollectionFill,
    CheckboxGroup,
    BFormTags,
    BFormSpinbutton
  }
})
export default class Preferences extends Vue {
  readonly NumberScope = NumberScope
  readonly CheckboxGroupType = CheckboxGroupType

  private isLoading = false
  private failedToLoad = false

  private trackingPoolIds: number[] = []
  private fixedNumberIds: number[] = []

  private trackingPoolOptions: { text: string; value: number }[] = []
  private fixedNumberOptions: { text: string; value: number }[] = []

  private numberScope = NumberScope.ALL_NUMBERS

  private lookbackInterval = LookbackIntervals[0]
  private lookbackIntervalOptions: Lookback[] = LookbackIntervals

  private lookbackHours = 720
  private sameDayMatching = false
  private lookForward = false
  private excludeDatetimeOnly = false
  private notificationEmails: string[] = []

  private lookbackSecondsInputMins = 0
  private lookbackSecondsInputSecs = 0

  get lookbackSeconds () {
    if (this.lookbackHours !== 0) {
      return 0
    }

    return (this.lookbackSecondsInputMins * 60) + this.lookbackSecondsInputSecs
  }

  private trackingPoolConfig: PreferencesConfig = {
    selected: [],
    excludeFixedNumbers: false
  }

  private fixedNumberConfig: PreferencesConfig = {
    selected: [],
    excludeFixedNumbers: false
  }

  async created () {
    await this.setupPreferences(true)
  }

  async setupPreferences (loadAll = false) {
    this.isLoading = true

    // Load preferences
    try {
      await this.$store.dispatch('getPreferences')
    } catch (e) {
      this.failedToLoad = true
      this.isLoading = false

      return
    }

    // Get tracking pools for the installation
    if (loadAll || !this.$store.state.trackingPools) {
      try {
        await this.$store.dispatch('getTrackingPoolIds')
        await this.$store.dispatch('getTrackingPools', {})
      } catch (e) {
        this.failedToLoad = true
        this.isLoading = false

        return
      }

      this.trackingPoolIds = this.$store.state.trackingPoolIds
    }

    // Get fixed numbers for the installation
    if (loadAll || !this.$store.state.fixedNumbers) {
      try {
        await this.$store.dispatch('getFixedNumberIds')
        await this.$store.dispatch('getFixedNumbers', {})
      } catch (e) {
        this.failedToLoad = true
        this.isLoading = false

        return
      }

      this.fixedNumberIds = this.$store.state.fixedNumberIds
    }

    this.numberScope = NumberScope[this.$store.state.preferences.configuration.numberScope as keyof typeof NumberScope]

    this.sameDayMatching = this.$store.state.preferences.configuration.sameDayMatching

    this.lookForward = this.$store.state.preferences.configuration.lookForward

    this.lookbackHours = this.$store.state.preferences.configuration.lookbackHours

    this.lookbackSecondsInputMins = Math.floor(this.$store.state.preferences.configuration.lookbackSeconds / 60)

    this.lookbackSecondsInputSecs = this.$store.state.preferences.configuration.lookbackSeconds - (this.lookbackSecondsInputMins * 60)

    this.excludeDatetimeOnly = this.$store.state.preferences.configuration.excludeDatetimeOnly

    this.notificationEmails = this.$store.state.preferences.configuration.notificationEmails

    const filteredLookbackIntervals = LookbackIntervals.filter(li => li.value === this.lookbackHours)
    if (filteredLookbackIntervals.length === 0) {
      const customInterval = { text: 'Custom', value: this.lookbackHours }
      this.lookbackInterval = customInterval
      this.lookbackIntervalOptions.push(customInterval)
    } else {
      this.lookbackInterval = filteredLookbackIntervals[0]
    }

    this.trackingPoolConfig = {
      selected: this.$store.state.preferences.configuration.trackingPoolIds,
      excludeFixedNumbers: this.$store.state.preferences.configuration.excludeFixedNumbers
    }

    this.fixedNumberConfig = {
      selected: this.$store.state.preferences.configuration.fixedNumberIds,
      excludeFixedNumbers: false
    }

    this.isLoading = false
  }

  @Watch('$store.state.trackingPools')
  setupTrackingPoolOptions (trackingPools: TrackingPool[]) {
    const trackingPoolOptions = []
    for (const trackingPool of trackingPools) {
      trackingPoolOptions.push({ text: trackingPool.getName(), value: parseInt(trackingPool.getId()) })
    }

    this.trackingPoolOptions = trackingPoolOptions
  }

  @Watch('$store.state.fixedNumbers')
  setupFixedNumberOptions (fixedNumbers: FixedNumber[]) {
    const fixedNumberOptions = []
    for (const fixedNumber of fixedNumbers) {
      fixedNumberOptions.push({
        text: fixedNumber.getDescription() ? `${fixedNumber.getDescription()} (${fixedNumber.getPhoneNumber()})` : `${fixedNumber.getPhoneNumber()}`,
        value: parseInt(fixedNumber.getId())
      })
    }

    this.fixedNumberOptions = fixedNumberOptions
  }

  updateNumberScope (numberScope: NumberScope) {
    this.numberScope = numberScope
  }

  updateNotificationEmails (notificationEmails: string[]) {
    this.notificationEmails = notificationEmails
  }

  updateLookback (lookback: Lookback) {
    this.lookbackInterval = lookback
    this.lookbackHours = lookback.value

    if (lookback.value !== 0) {
      this.lookbackSecondsInputMins = 0
      this.lookbackSecondsInputSecs = 0
    }
  }

  unsetLookForward () {
    // value hasn't updated yet so check for off -> on
    if (!this.sameDayMatching) {
      this.lookForward = false

      this.$nextTick()
    }
  }

  async doSave () {
    const updatePreferenceRequest: Partial<UpdatePreferenceRequest> = {
      numberScope: Object.keys(NumberScope).find((key) => { return NumberScope[key as keyof typeof NumberScope] === this.numberScope }) as NumberScope,
      trackingPoolIds: this.trackingPoolConfig.selected,
      fixedNumberIds: this.fixedNumberConfig.selected,
      excludeFixedNumbers: this.trackingPoolConfig.excludeFixedNumbers,
      sameDayMatching: this.sameDayMatching,
      lookForward: this.lookForward,
      excludeDatetimeOnly: this.excludeDatetimeOnly,
      notificationEmails: this.notificationEmails
    }

    if (this.lookbackSeconds === 0) {
      updatePreferenceRequest.lookbackHours = this.lookbackHours
    }

    if (this.lookbackHours === 0) {
      updatePreferenceRequest.lookbackSeconds = this.lookbackSeconds
    }

    try {
      await this.$store.dispatch('updatePreferences', { updatePreferenceRequest })

      this.$bvToast.toast('Your preferences have been saved.', {
        title: 'Success',
        variant: 'success',
        autoHideDelay: 5000
      })
    } catch (e) {
      this.$bvToast.toast('We encountered a problem saving your preferences. Please try again.', {
        title: 'Error',
        variant: 'danger',
        autoHideDelay: 5000
      })
    }

    await this.$nextTick()
  }

  validateEmail (tag: string): boolean {
    // borrowed from https://html.spec.whatwg.org/multipage/input.html#email-state-(type=email)
    return /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/.test(tag)
  }
}
</script>

<style lang="scss">

</style>
